import { useRef, useState, useEffect, useCallback } from 'react';
import { X, SwitchCamera, Camera, Flashlight, FlashlightOff } from 'lucide-react';

/**
 * Full-screen camera capture with aspect ratio crop overlay.
 * Crops the captured image to the specified aspect ratio.
 */
function CameraCapture({ aspectW = 3, aspectH = 1, outputWidth = 600, onCapture, onCancel }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [ready, setReady] = useState(false);
  const [hasStream, setHasStream] = useState(false);
  const [error, setError] = useState(null);
  const [flashOn, setFlashOn] = useState(true);

  const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  // Request camera — triggers permission prompt
  const requestCamera = useCallback(async (facing) => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    setError(null);
    setFlashOn(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        try { 
          await video.play(); 
          setReady(true);
          // Enable flash by default after camera is ready
          const videoTrack = stream.getVideoTracks()[0];
          if (videoTrack) {
            const capabilities = videoTrack.getCapabilities();
            if (capabilities.torch) {
              try {
                await videoTrack.applyConstraints({ advanced: [{ torch: true }] });
                setFlashOn(true);
              } catch (err) {
                setFlashOn(false);
              }
            } else {
              setFlashOn(false);
            }
          }
        } catch { setHasStream(true); }
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError(!isSecure
          ? 'Camera requires a secure connection (HTTPS).'
          : 'Camera permission denied. Tap the camera icon in your address bar to allow, then tap Retry.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found.');
      } else if (err.name === 'NotReadableError') {
        setError('Camera is in use by another app. Close it and tap Retry.');
      } else {
        setError('Could not access camera. Tap Retry.');
      }
    }
  }, [isSecure]);

  // Auto-request on mount and facing mode change
  useEffect(() => {
    requestCamera(facingMode);
    return () => { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  }, [facingMode, requestCamera]);

  // Fallback: start playback from user gesture if auto-play was blocked
  const startPlayback = async () => {
    const video = videoRef.current;
    if (!video || !streamRef.current) return;
    try { await video.play(); setReady(true); } catch (err) { /* Error handled */ }
  };

  const cleanup = useCallback(() => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  const flip = () => {
    setFacingMode((f) => f === 'environment' ? 'user' : 'environment');
    setReady(false);
    setHasStream(false);
  };

  const toggleFlash = async () => {
    if (!streamRef.current) return;
    
    const videoTrack = streamRef.current.getVideoTracks()[0];
    if (!videoTrack) return;

    const capabilities = videoTrack.getCapabilities();
    if (!capabilities.torch) {
      return;
    }

    try {
      await videoTrack.applyConstraints({
        advanced: [{ torch: !flashOn }]
      });
      setFlashOn(!flashOn);
    } catch (err) {
    }
  };

  // Take photo from live camera stream
  const takePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const overlay = overlayRef.current;
    if (!video || !canvas || !overlay) {
      return;
    }

    const overlayRect = overlay.getBoundingClientRect();
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const videoRect = video.getBoundingClientRect();

    // With object-cover, video is uniformly scaled to cover the element (no stretching)
    // Calculate the uniform scale factor and content offset within the element
    const s = Math.max(videoRect.width / vw, videoRect.height / vh);
    const displayW = vw * s;
    const displayH = vh * s;
    const offsetX = (videoRect.width - displayW) / 2;
    const offsetY = (videoRect.height - displayH) / 2;

    // Calculate crop area in video pixel coordinates
    const cropX = (overlayRect.left - videoRect.left - offsetX) / s;
    const cropY = (overlayRect.top - videoRect.top - offsetY) / s;
    const cropW = overlayRect.width / s;
    const cropH = overlayRect.height / s;

    const targetWidth = outputWidth;
    const targetHeight = Math.round(outputWidth / (aspectW / aspectH));
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, targetWidth, targetHeight);

    canvas.toBlob((blob) => {
      if (!blob) {
        return;
      }
      const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
      cleanup();
      onCapture(file);
    }, 'image/jpeg', 0.85);
  };

  const handleCancel = () => { cleanup(); onCancel(); };

  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-black">
      <video ref={videoRef} playsInline muted className="absolute inset-0 h-full w-full object-cover" />

      {/* Dark mask with cutout for aspect ratio */}
      {ready && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-5">
          <div
            ref={overlayRef}
            className="border-2 border-white/80 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.55)] w-full box-border"
            style={{ aspectRatio: `${aspectW} / ${aspectH}` }}
          />
        </div>
      )}

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-4 py-3">
        <button onClick={handleCancel} className="flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white">
          <X className="h-6 w-6" />
        </button>
        <span className="text-sm font-medium text-white">
          {ready ? 'Position test in frame' : 'Camera'}
        </span>
        {ready ? (
          <div className="flex items-center gap-2">
            <button onClick={toggleFlash} className="flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white">
              {flashOn ? <Flashlight className="h-5 w-5" /> : <FlashlightOff className="h-5 w-5" />}
            </button>
            <button onClick={flip} className="flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white">
              <SwitchCamera className="h-5 w-5" />
            </button>
          </div>
        ) : <div className="w-10" />}
      </div>

      <div className="flex-1" />

      {/* Error message with retry */}
      {error && !hasStream && (
        <div className="relative z-10 mx-4 mb-4 flex flex-col items-center gap-3">
          <div className="rounded-lg bg-red-900/80 px-4 py-3 text-center text-sm text-white">{error}</div>
          <button onClick={() => requestCamera(facingMode)} className="rounded-full bg-white/20 px-6 py-2 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/30">Retry</button>
        </div>
      )}

      {/* Stream obtained but auto-play blocked — tap to start */}
      {hasStream && !ready && (
        <div className="relative z-10 flex flex-col items-center gap-4 pb-8">
          <button onClick={startPlayback} className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-white bg-white/20 transition hover:bg-white/30">
            <Camera className="h-8 w-8 text-white" />
          </button>
          <span className="text-sm text-white/80">Tap to start camera</span>
        </div>
      )}

      {/* No stream yet — show loading */}
      {!hasStream && !error && (
        <div className="relative z-10 flex flex-col items-center gap-4 pb-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
        </div>
      )}

      {/* Capture button — shown once camera is live */}
      {ready && (
        <div className="relative z-10 flex items-center justify-center pb-8">
          <button onClick={takePhoto} disabled={!ready} className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-white bg-white/20 transition hover:bg-white/30 disabled:opacity-40">
            <div className="h-12 w-12 rounded-full bg-white/90" />
          </button>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}

export default CameraCapture;
